import { createRoot, hydrateRoot } from 'react-dom/client'
import './index.css'
import App from './App'
import './brand-refresh.scss'

const CHUNK_RECOVERY_STORAGE_KEY = 'dihaw.chunk-load-recovery-at'
const CHUNK_RECOVERY_COOLDOWN_MS = 45_000

function isDynamicImportFailureMessage(message: string) {
  const normalizedMessage = String(message || '').toLowerCase()
  if (!normalizedMessage) {
    return false
  }
  return (
    normalizedMessage.includes('failed to fetch dynamically imported module')
    || normalizedMessage.includes('importing a module script failed')
  )
}

function shouldTriggerChunkRecovery() {
  if (typeof window === 'undefined') {
    return false
  }

  const previousAt = Number(window.sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY) || '0')
  const now = Date.now()
  if (Number.isFinite(previousAt) && previousAt > 0 && (now - previousAt) < CHUNK_RECOVERY_COOLDOWN_MS) {
    return false
  }

  window.sessionStorage.setItem(CHUNK_RECOVERY_STORAGE_KEY, String(now))
  return true
}

function registerChunkLoadRecovery() {
  if (typeof window === 'undefined') {
    return
  }

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    if (!shouldTriggerChunkRecovery()) {
      return
    }
    window.location.reload()
  })

  window.addEventListener('error', (event) => {
    if (!isDynamicImportFailureMessage(event.message)) {
      return
    }
    if (!shouldTriggerChunkRecovery()) {
      return
    }
    window.location.reload()
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message = reason instanceof Error
      ? reason.message
      : String(reason || '')
    if (!isDynamicImportFailureMessage(message)) {
      return
    }
    if (!shouldTriggerChunkRecovery()) {
      return
    }
    window.location.reload()
  })
}

function scheduleManifestHydration() {
  if (typeof window === 'undefined') {
    return
  }

  const injectManifest = () => {
    if (document.querySelector('link[rel="manifest"]')) {
      return
    }
    const link = document.createElement('link')
    link.rel = 'manifest'
    link.href = '/manifest.webmanifest'
    document.head.appendChild(link)
  }

  if (document.readyState === 'complete') {
    window.setTimeout(injectManifest, 1600)
    return
  }

  window.addEventListener('load', () => {
    window.setTimeout(injectManifest, 1600)
  }, { once: true })
}

scheduleManifestHydration()
registerChunkLoadRecovery()

const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error('Root element not found.')
}

const appTree = <App />

const hasBootShellMarkup = rootElement.getAttribute('data-boot-shell') === '1'

if (hasBootShellMarkup) {
  rootElement.removeAttribute('data-boot-shell')
  rootElement.innerHTML = ''
  createRoot(rootElement).render(appTree)
} else if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, appTree)
} else {
  createRoot(rootElement).render(appTree)
}
